import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import Container from '@mui/material/Container';
import MuiLink from '@mui/material/Link';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import SettingsIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import DialpadIcon from '@mui/icons-material/Dialpad';
import MessageIcon from '@mui/icons-material/TextsmsOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { hideMobileNav, showMobileNav } from 'store/modules/modals/actions';
import { styled } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import Alert from '@mui/material/Alert';
import {
  bindTrigger,
  bindPopover,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import NotificationsPopover from 'modules/notifications/components/NotificationsPopover';
import { stringAvatar } from 'utils';
import Button from 'components/Button';
import { toggleDialer } from 'store/modules/modals/actions';
import { logout, setTheme } from '../store/modules/user/actions';
import UpgradeBanner from './UpgradeBanner';
import LearnMenu from './LearnMenu';
import FEATURES from '../constants/features';
import WithFeature from './WithFeature';
import Access, { ACTIONS, MODULES } from './Access';

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer - 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `100%`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const AppHeader = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((s) => s.user.user);
  const account = useSelector((s) => s.settings.account);
  const showDrawer = useSelector((state) => state.modals.showMobileNav);
  const showDialer = useSelector((state) => state.modals.showDialer);
  const isDarkMode = useSelector((s) => s.user.darkMode);
  const token = user.twilio_browser_calling_token;

  const menuState = usePopupState({
    variant: 'popover',
    popupId: 'user',
  });
  const notificationPopoverState = usePopupState({
    variant: 'popover',
    popupId: 'notification',
  });
  const notifications = useSelector((state) => state.notifications.data);

  const toggleDrawer = () => {
    dispatch(showDrawer ? hideMobileNav() : showMobileNav());
  };

  const onToggleTheme = () => {
    dispatch(setTheme(isDarkMode ? false : true));
  };

  const onLogout = () => {
    dispatch(logout());
    window.location.href = '/login';
  };

  const openIntercom = () => {
    if (window.Intercom) {
      window.Intercom('show');
    }
  };

  const onToggleDialer = () => {
    dispatch(toggleDialer(!showDialer));
  };

  return (
    <AppBar
      elevation={0}
      position="relative"
      open={showDrawer}
      color="inherit"
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: 1200,
      }}
    >
      <Container maxWidth={false}>
        <Toolbar disableGutters>
          <Button
            onClick={toggleDrawer}
            color="inherit"
            size="small"
            sx={{
              'mr': 1,
              'color': 'white',
              'bgcolor': 'grey.500',
              'minWidth': 'auto',
              'borderRadius': 0.5,
              '&:hover': {
                bgcolor: 'grey.600',
              },
            }}
          >
            {showDrawer ? (
              <ArrowBackIcon fontSize="small" />
            ) : (
              <ArrowForwardIcon fontSize="small" />
            )}
          </Button>
          <UpgradeBanner location={location} />
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <Access
              showError={false}
              action={ACTIONS.MODIFY}
              module={MODULES.MESSAGES}
            >
              <Button
                sx={{ mr: 1 }}
                startIcon={<MessageIcon fontSize="small" />}
                variant="outlined"
                component={Link}
                to={`/messages/create`}
              >
                New Message
              </Button>
            </Access>
            {token && (
              <WithFeature
                feature={FEATURES.TALK_DIGITAL_RECEPTION}
                message={false}
              >
                <IconButton
                  active={showDialer}
                  color="primary"
                  size="large"
                  onClick={onToggleDialer}
                >
                  <DialpadIcon fontSize="small" />
                </IconButton>
              </WithFeature>
            )}
            <LearnMenu />
            <IconButton
              {...bindTrigger(notificationPopoverState)}
              color="primary"
              size="large"
            >
              <Badge
                color="secondary"
                variant="dot"
                overlap="circular"
                showZero={false}
                badgeContent={notifications.length}
              >
                <NotificationsNoneOutlinedIcon />
              </Badge>
            </IconButton>
            <IconButton
              color="primary"
              size="large"
              onClick={openIntercom}
              sx={{ mr: 1 }}
            >
              <HelpOutlineOutlinedIcon />
            </IconButton>
            <Popover
              {...bindPopover(notificationPopoverState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <NotificationsPopover
                account={account}
                popupState={notificationPopoverState}
              />
            </Popover>
            <Divider orientation="vertical" variant="middle" flexItem />
            <IconButton {...bindTrigger(menuState)} sx={{ p: 0, ml: 2 }}>
              <Avatar
                {...stringAvatar(
                  user.user_first_name + ' ' + user.user_last_name,
                  {
                    width: 36,
                    height: 36,
                    fontSize: '16px',
                  },
                )}
              />
            </IconButton>
            <Popover
              {...bindPopover(menuState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box p={2}>
                <ListItem
                  sx={{ mb: 3 }}
                  button
                  component={Link}
                  to={`/settings/profile`}
                  onClick={() => menuState.close()}
                >
                  <ListItemAvatar>
                    <Avatar
                      {...stringAvatar(
                        user.user_first_name + ' ' + user.user_last_name,
                      )}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 600,
                    }}
                    primary={user.user_first_name + ' ' + user.user_last_name}
                    secondary={user.user_username}
                  />
                </ListItem>
                <ListItem button onClick={onToggleTheme} sx={{ minHeight: 60 }}>
                  <ListItemIcon>
                    {isDarkMode ? (
                      <Brightness7Icon color="primary" />
                    ) : (
                      <Brightness4Icon color="primary" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={isDarkMode ? 'Light Theme' : 'Dark Theme'}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <Divider />
                <ListItem button onClick={onLogout} sx={{ minHeight: 60 }}>
                  <ListItemIcon>
                    <LogoutOutlinedIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Log out"
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              </Box>
            </Popover>
          </Box>
        </Toolbar>
      </Container>
      {account.membership_status?.status_system_name ===
        'MEMBER_FAILED_PAYMENT' && (
        <Alert
          severity="error"
          sx={{ justifyContent: 'center', borderRadius: 0 }}
        >
          Attention: Your subscription payment has failed. Please update your
          payment method under <Link to="/settings/payment">Settings</Link>.
          Contact{' '}
          <MuiLink to="mailto:support@textinchurch.com">
            support@textinchurch.com
          </MuiLink>{' '}
          for assistance.
        </Alert>
      )}
    </AppBar>
  );
};

export default AppHeader;
